import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bot,
  ClipboardCheck,
  Coins,
  FilePenLine,
  FolderOpen,
  ListChecks,
  Lock,
  type LucideIcon,
  Menu,
  PenLine,
  ScanLine,
  ShieldCheck,
  UserCheck,
  UserRound,
  X,
} from "lucide-react";
import {
  fetchReaderInbox,
  type NevisignDocumentListItem,
  type ReaderInboxTask,
} from "@nevisign/reader-core";
import {
  TASK_SOURCE_LABELS_FA,
  TASK_STATUS_LABELS_FA,
  type SystemTaskStatus,
  type TaskSource,
} from "@platform/shared-types";
import { HomeToolsGrid } from "./ToolsMenu";
import { IdentityBanner } from "./esign/IdentityBanner";
import { READER_TOOLS, type ToolId } from "../lib/readerTools";
import { useAuth } from "../contexts/auth-context";
import { ensureFreshAccessToken } from "../lib/platform-auth";
import { requestAuthForAction } from "../lib/auth-required";
import { listLocalDrafts, type LocalDraft } from "../lib/local-drafts";
import {
  listScannedDocumentSummaries,
  type ScannedDocSummary,
} from "../lib/scan-library";
import {
  formatTokenFa,
  getDisplayTokenBalance,
} from "../lib/token-balance";
import { fetchEsignProfile } from "../lib/esign/api";
import { loadEsignLocal } from "../lib/esign/local-store";

export type KartablOpenPayload = {
  task: ReaderInboxTask;
  document: NevisignDocumentListItem;
};

export type DadnoActionMode = "summary" | "risk" | "consult";

type Props = {
  apiBase: string;
  onSelectTool: (id: ToolId) => void;
  onOpenKartablTask: (payload: KartablOpenPayload) => void;
  openFilePicker: () => void;
  openingDocId?: string | null;
  onOpenContractHub: () => void;
  onOpenDadnoAction: (mode: DadnoActionMode) => void;
  onOpenDocuments: () => void;
  onOpenCases: () => void;
  onOpenSignStart: () => void;
  onOpenLocalDraft: (draft: LocalDraft) => void;
  onOpenScannedDoc?: (docId: string) => void;
  onOpenScanLibrary?: () => void;
  /** Hamburger lives in the hero while App chrome is hidden at scroll=0. */
  onToggleMenu: () => void;
  menuOpen?: boolean;
  onOpenProfile?: () => void;
  showEsignBanner?: boolean;
  onStartEsign?: () => void;
  onDismissEsign?: () => void;
};

type FeatureChip = {
  label: string;
  Icon?: LucideIcon;
};

type ProductCardDef = {
  title: string;
  subtitle?: string;
  features: FeatureChip[];
  onClick: () => void;
  imageSrc: string;
  badge?: string;
  accent?: "default" | "dadno" | "sign" | "locked" | "draft" | "view" | "docs" | "cases";
};

type QuickAction = {
  label: string;
  onClick: () => void;
  imageSrc: string;
  tone: string;
  locked?: boolean;
};

const REVIEW_TASK_SOURCES = new Set(["contract_approval", "contract_signature"]);
const CARTABLE_PREVIEW_LIMIT = 4;

function toPersianDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]!);
}

function faNum(n: number): string {
  return toPersianDigits(n.toLocaleString("en-US"));
}

function sourceBadgeLabel(source: string): string {
  return TASK_SOURCE_LABELS_FA[source as TaskSource] ?? source;
}

function isReviewTask(task: ReaderInboxTask): boolean {
  return REVIEW_TASK_SOURCES.has(task.source);
}

function taskToDocumentStub(task: ReaderInboxTask): NevisignDocumentListItem {
  return {
    id: task.contractId!,
    title: task.title,
    kind: "contract",
    kind_label: "قرارداد",
    status: task.status,
    status_label:
      TASK_STATUS_LABELS_FA[task.status as SystemTaskStatus] ?? task.status,
    subject: "",
    jurisdiction: "",
    parties_summary: "",
    created_at: "",
    updated_at: "",
    progress_message: "",
    signed: false,
  };
}

function KartablTaskRow({
  task,
  onOpen,
  opening,
}: {
  task: ReaderInboxTask;
  onOpen: (task: ReaderInboxTask) => void;
  opening: boolean;
}) {
  const isApproval = task.source === "contract_approval";
  const isSignature = task.source === "contract_signature";
  const canOpenDoc = Boolean(task.contractId);
  const dueLabel = task.dueAtJalali
    ? toPersianDigits(task.dueAtJalali)
    : null;

  return (
    <li
      className={`kartabl-task-row compact${task.overdue ? " overdue" : ""}${isApproval ? " approval" : ""}${isSignature ? " signature" : ""}`}
    >
      <button
        type="button"
        className="kartabl-task-main as-button compact-row"
        disabled={!canOpenDoc || opening}
        onClick={() => onOpen(task)}
      >
        <span className="kartabl-task-copy">
          <span className="kartabl-task-title">{task.title}</span>
          {dueLabel ? (
            <small className={`kartabl-task-due${task.overdue ? " is-overdue" : ""}`}>
              مهلت {dueLabel}
            </small>
          ) : null}
        </span>
        <span className="kartabl-task-badge">
          {isSignature ? <PenLine size={12} /> : null}
          {isApproval ? <ShieldCheck size={12} /> : null}
          {task.overdue ? "عقب‌افتاده" : sourceBadgeLabel(task.source)}
        </span>
      </button>
    </li>
  );
}

function CartablePane({
  title,
  count,
  Icon,
  loading,
  emptyTitle,
  emptyHint,
  tasks,
  openingDocId,
  onOpen,
  onSeeAll,
  seeAllLabel,
}: {
  title: string;
  count: number;
  Icon: LucideIcon;
  loading: boolean;
  emptyTitle: string;
  emptyHint: string;
  tasks: ReaderInboxTask[];
  openingDocId?: string | null;
  onOpen: (task: ReaderInboxTask) => void;
  onSeeAll?: () => void;
  seeAllLabel?: string;
}) {
  return (
    <div className="dashboard-cartable-pane">
      <div className="dashboard-cartable-pane-head">
        <div className="dashboard-cartable-pane-title">
          <Icon size={14} aria-hidden />
          <h3>{title}</h3>
        </div>
        <span className="dashboard-cartable-count" aria-label={`${faNum(count)} مورد`}>
          {faNum(count)}
        </span>
      </div>

      {loading ? (
        <div className="dashboard-skeleton-list compact">
          <div className="dashboard-skeleton-row" />
          <div className="dashboard-skeleton-row" />
        </div>
      ) : tasks.length ? (
        <>
          <ul className="kartabl-task-list compact">
            {tasks.map((task) => (
              <KartablTaskRow
                key={task.id}
                task={task}
                onOpen={onOpen}
                opening={openingDocId === task.contractId}
              />
            ))}
          </ul>
          {onSeeAll && seeAllLabel ? (
            <button type="button" className="dashboard-cartable-more" onClick={onSeeAll}>
              {seeAllLabel}
            </button>
          ) : null}
        </>
      ) : (
        <div className="dashboard-empty compact">
          <p className="dashboard-empty-title">{emptyTitle}</p>
          <p>{emptyHint}</p>
        </div>
      )}
    </div>
  );
}

function DashboardProductCard({
  title,
  subtitle,
  features,
  onClick,
  imageSrc,
  badge,
  accent = "default",
}: ProductCardDef) {
  const isLocked = accent === "locked";

  return (
    <button
      type="button"
      className={`service-entry-card dashboard-product-card accent-${accent}${isLocked ? " is-locked" : ""}`}
      onClick={onClick}
    >
      <div className="dashboard-product-body">
        <div className="dashboard-product-head">
          <div className="dashboard-product-visual" aria-hidden>
            <img
              className={`dashboard-product-illustration${accent === "dadno" ? " is-dadno-logo" : ""}`}
              src={imageSrc}
              alt=""
              width={64}
              height={64}
              decoding="async"
            />
          </div>
          <div className="dashboard-product-head-text">
            {badge ? (
              <span className="dashboard-product-badge login-required">
                <Lock size={11} aria-hidden />
                {badge}
              </span>
            ) : null}
            <strong className="dashboard-product-title">{title}</strong>
            {subtitle ? (
              <p className="dashboard-product-subtitle">{subtitle}</p>
            ) : null}
          </div>
        </div>

        <ul className="dashboard-feature-chips" aria-label={`ویژگی‌های ${title}`}>
          {features.map((feature) => {
            const FeatureIcon = feature.Icon;
            return (
              <li key={feature.label} className="dashboard-feature-chip">
                {FeatureIcon ? <FeatureIcon size={9} aria-hidden /> : null}
                <span>{feature.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </button>
  );
}

export function HomeDashboard({
  apiBase,
  onSelectTool,
  onOpenKartablTask,
  openFilePicker,
  openingDocId,
  onOpenContractHub,
  onOpenDadnoAction,
  onOpenDocuments,
  onOpenCases,
  onOpenSignStart,
  onOpenLocalDraft,
  onOpenScannedDoc,
  onOpenScanLibrary,
  onToggleMenu,
  menuOpen = false,
  onOpenProfile,
  showEsignBanner = false,
  onStartEsign,
  onDismissEsign,
}: Props) {
  const { isAuthenticated, user, openLogin } = useAuth();
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inbox, setInbox] = useState<Awaited<ReturnType<typeof fetchReaderInbox>> | null>(
    null,
  );
  const [localDrafts, setLocalDrafts] = useState<LocalDraft[]>([]);
  const [scannedDocs, setScannedDocs] = useState<ScannedDocSummary[]>([]);
  const [esignStatus, setEsignStatus] = useState<{
    identityVerified: boolean;
    hasRootCertificate: boolean;
  } | null>(null);

  const loadInbox = useCallback(async () => {
    if (!isAuthenticated) {
      setInbox(null);
      return;
    }
    setInboxLoading(true);
    await ensureFreshAccessToken();
    const result = await fetchReaderInbox(apiBase, { limit: 10 });
    setInbox(result);
    setInboxLoading(false);
    if (result.error?.includes("وارد")) {
      requestAuthForAction(() => void loadInbox());
    }
  }, [apiBase, isAuthenticated]);

  useEffect(() => {
    setLocalDrafts(listLocalDrafts());
    void listScannedDocumentSummaries().then(setScannedDocs);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setInbox(null);
      return;
    }
    void loadInbox();
  }, [isAuthenticated, loadInbox]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setEsignStatus(null);
      return;
    }
    const local = loadEsignLocal(user.id);
    if (local) {
      setEsignStatus({
        identityVerified: local.identityVerified,
        hasRootCertificate: local.hasRootCertificate,
      });
    }
    let cancelled = false;
    void fetchEsignProfile(apiBase)
      .then((profile) => {
        if (cancelled) return;
        setEsignStatus({
          identityVerified: profile.identityVerified,
          hasRootCertificate: profile.hasRootCertificate,
        });
      })
      .catch(() => {
        /* keep local cache if present */
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, isAuthenticated, user?.id]);

  const inboxCounts = useMemo(() => {
    const items = inbox?.tasks ?? [];
    const review = items.filter(isReviewTask);
    const tasks = items.filter((t) => !isReviewTask(t));
    return {
      total: items.length,
      review: review.length,
      tasks: tasks.length,
      overdue: items.filter((t) => t.overdue).length,
      approval: items.filter((t) => t.source === "contract_approval").length,
      signature: items.filter((t) => t.source === "contract_signature").length,
    };
  }, [inbox?.tasks]);

  const prioritizedTasks = useMemo(() => {
    return [...(inbox?.tasks ?? [])].sort((a, b) => {
      if (a.overdue !== b.overdue) return Number(b.overdue) - Number(a.overdue);
      if (Boolean(a.dueAtJalali) !== Boolean(b.dueAtJalali)) {
        return Number(Boolean(b.dueAtJalali)) - Number(Boolean(a.dueAtJalali));
      }
      return 0;
    });
  }, [inbox?.tasks]);

  const reviewTasks = useMemo(
    () => prioritizedTasks.filter(isReviewTask),
    [prioritizedTasks],
  );
  const generalTasks = useMemo(
    () => prioritizedTasks.filter((t) => !isReviewTask(t)),
    [prioritizedTasks],
  );

  const tokenBalance = useMemo(() => {
    if (!isAuthenticated || !user?.id) return null;
    try {
      return getDisplayTokenBalance({
        isAuthenticated: true,
        userId: user.id,
      });
    } catch {
      return null;
    }
  }, [isAuthenticated, user?.id]);

  const visibleReviewTasks = reviewTasks.slice(0, CARTABLE_PREVIEW_LIMIT);
  const visibleGeneralTasks = generalTasks.slice(0, CARTABLE_PREVIEW_LIMIT);
  const visibleLocalDrafts = localDrafts.slice(0, 2);
  const hiddenLocalDrafts = localDrafts.slice(2);
  const heroGreeting = user?.firstName
    ? `${user.firstName} عزیز، خوش آمدید`
    : "خوش آمدید";
  const identityStatusLabel = !isAuthenticated
    ? "وارد نشده‌اید"
    : esignStatus
      ? `${esignStatus.identityVerified ? "احراز هویت شده" : "احراز هویت نشده"} · ${
          esignStatus.hasRootCertificate ? "گواهی امضا دارد" : "گواهی امضا ندارد"
        }`
      : "در حال بررسی وضعیت امضا…";
  const cartableSubtitle =
    inboxCounts.total > 0
      ? `${faNum(inboxCounts.total)} مورد فعال در کارتابل`
      : "کارهای باز و اسناد نیازمند بررسی";
  const inboxReady = Boolean(inbox);
  const showCartableLoading = inboxLoading && !inboxReady;

  const quickActions = useMemo<QuickAction[]>(
    () => [
      {
        label: "تدوین قرارداد",
        onClick: onOpenContractHub,
        imageSrc: "/illustrations/service-draft.webp",
        tone: "draft",
      },
      {
        label: "مشاهده اسناد",
        onClick: openFilePicker,
        imageSrc: "/illustrations/service-view.webp",
        tone: "view",
      },
      {
        label: "امضای اسناد",
        onClick: onOpenSignStart,
        imageSrc: "/illustrations/service-sign.webp",
        tone: "sign",
      },
      {
        label: "اسناد و قراردادها",
        onClick: isAuthenticated
          ? onOpenDocuments
          : () => openLogin(() => onOpenDocuments()),
        imageSrc: "/illustrations/service-docs.webp",
        tone: "docs",
        locked: !isAuthenticated,
      },
    ],
    [
      isAuthenticated,
      onOpenContractHub,
      onOpenDocuments,
      onOpenSignStart,
      openFilePicker,
      openLogin,
    ],
  );

  const handleOpenTask = useCallback(
    (task: ReaderInboxTask) => {
      if (!task.contractId) return;
      onOpenKartablTask({ task, document: taskToDocumentStub(task) });
    },
    [onOpenKartablTask],
  );

  const handleSelectTool = useCallback(
    (id: ToolId) => {
      const def = READER_TOOLS.find((t) => t.id === id);
      if (def?.requireAuth && !isAuthenticated) {
        openLogin(() => onSelectTool(id));
        return;
      }
      onSelectTool(id);
    },
    [isAuthenticated, onSelectTool, openLogin],
  );

  const casesProduct = useMemo<ProductCardDef>(
    () => ({
      title: "پرونده‌های قضایی",
      features: [
        { label: "فرایندها", Icon: FolderOpen },
        { label: "هوش مصنوعی", Icon: Bot },
        { label: "عدل ایران", Icon: ShieldCheck },
      ],
      onClick: isAuthenticated
        ? onOpenCases
        : () => openLogin(() => onOpenCases()),
      imageSrc: "/illustrations/service-cases.webp",
      accent: isAuthenticated ? "cases" : "locked",
      badge: isAuthenticated ? undefined : "نیاز به ورود",
    }),
    [isAuthenticated, onOpenCases, openLogin],
  );

  const dadnoActions = useMemo(
    () =>
      [
        {
          mode: "summary" as const,
          label: "خلاصه اسناد حقوقی",
          imageSrc: "/illustrations/dadno-action-summary.png",
        },
        {
          mode: "risk" as const,
          label: "تحلیل ریسک قراردادها",
          imageSrc: "/illustrations/dadno-action-risk.png",
        },
        {
          mode: "consult" as const,
          label: "مشاوره هوشمند",
          imageSrc: "/illustrations/dadno-action-consult.png",
        },
      ] as const,
    [],
  );

  return (
    <div className="home-dashboard home-dashboard-paraph">
      <section className="dashboard-hero-band" aria-label="خانه نویساین">
        <div className="dashboard-hero-band-top">
          <div className="dashboard-hero-brand">
            {/* RTL: first flex child sits on the right → hamburger to the right of logo */}
            <button
              type="button"
              className="dashboard-hero-menu-btn"
              aria-label="منو"
              aria-expanded={menuOpen}
              onClick={onToggleMenu}
            >
              {menuOpen ? <X size={18} aria-hidden /> : <Menu size={18} aria-hidden />}
            </button>
            <img
              className="dashboard-hero-logo"
              src="/brand/nevisign-logo.svg"
              alt="نویساین"
              width={96}
              height={84}
              decoding="async"
            />
          </div>
          <div className="dashboard-hero-top-actions">
            {isAuthenticated && tokenBalance != null ? (
              <span
                className="dashboard-hero-token-chip"
                title="مانده توکن"
                aria-label={`${formatTokenFa(tokenBalance)} توکن باقی‌مانده`}
              >
                <Coins size={13} aria-hidden />
                <strong>{formatTokenFa(tokenBalance)}</strong>
                <span>توکن</span>
              </span>
            ) : null}
            {!isAuthenticated ? (
              <button
                type="button"
                className="dashboard-hero-login-chip"
                onClick={() => openLogin()}
              >
                ورود
              </button>
            ) : (
              <button
                type="button"
                className="dashboard-hero-icon-btn"
                aria-label="پروفایل کاربری"
                onClick={() => onOpenProfile?.()}
              >
                <UserRound size={16} aria-hidden />
              </button>
            )}
          </div>
        </div>

        {!isAuthenticated ? (
          <div className="dashboard-hero-guest">
            <p className="dashboard-hero-org">{identityStatusLabel}</p>
            <p className="dashboard-hero-copy">
              برای مشاهده <strong>کارتابل امضای اسناد</strong> و استفاده از{" "}
              <strong>خدمات تکمیلی</strong> وارد شوید.
            </p>
            <button
              type="button"
              className="primary-btn dashboard-hero-cta"
              onClick={() => openLogin()}
            >
              ورود / ثبت‌نام
            </button>
          </div>
        ) : (
          <div className="dashboard-hero-auth">
            <h1 className="dashboard-hero-greeting">{heroGreeting}</h1>
            <p className="dashboard-hero-org">{identityStatusLabel}</p>
          </div>
        )}
      </section>

      <section className="dashboard-quick-card" aria-label="میانبر خدمات">
        {quickActions.map((action) => (
          <button
            key={action.label}
            type="button"
            className={`dashboard-quick-action tone-${action.tone}${action.locked ? " is-locked" : ""}`}
            onClick={action.onClick}
          >
            <span className="dashboard-quick-icon" aria-hidden>
              <img
                className="dashboard-quick-illustration"
                src={action.imageSrc}
                alt=""
                width={40}
                height={40}
                decoding="async"
              />
              {action.locked ? (
                <span className="dashboard-quick-lock" title="نیاز به ورود">
                  <Lock size={10} aria-hidden />
                </span>
              ) : null}
            </span>
            <span className="dashboard-quick-label">{action.label}</span>
          </button>
        ))}
      </section>

      {showEsignBanner && onStartEsign ? (
        <div className="dashboard-esign-banner-wrap">
          <IdentityBanner onStart={onStartEsign} onDismiss={onDismissEsign} />
        </div>
      ) : null}

      {isAuthenticated ? (
        <section
          className="dashboard-section dashboard-cartable-block"
          aria-label="کارتابل"
        >
          <div className="dashboard-section-head dashboard-section-head-tight">
            <div className="dashboard-section-title">
              <ClipboardCheck size={16} aria-hidden />
              <div>
                <h2>کارتابل من</h2>
                <p>{cartableSubtitle}</p>
              </div>
            </div>
            <button
              type="button"
              className="dashboard-cartable-all"
              onClick={onOpenDocuments}
            >
              همه کارها
            </button>
          </div>

          {!showCartableLoading ? (
            <div className="dashboard-summary-row" aria-label="خلاصه کارتابل">
              <span className="dashboard-summary-pill strong">
                {faNum(inboxCounts.total)} فعال
              </span>
              {inboxCounts.tasks > 0 ? (
                <span className="dashboard-summary-pill">
                  {faNum(inboxCounts.tasks)} وظیفه
                </span>
              ) : null}
              {inboxCounts.approval > 0 ? (
                <span className="dashboard-summary-pill count-approval">
                  {faNum(inboxCounts.approval)} تأیید
                </span>
              ) : null}
              {inboxCounts.signature > 0 ? (
                <span className="dashboard-summary-pill count-signature">
                  {faNum(inboxCounts.signature)} امضا
                </span>
              ) : null}
              {inboxCounts.overdue > 0 ? (
                <span className="dashboard-summary-pill count-overdue">
                  {faNum(inboxCounts.overdue)} عقب‌افتاده
                </span>
              ) : null}
            </div>
          ) : null}

          {inbox?.error ? (
            <div className="dashboard-empty compact">
              <p className="dashboard-empty-title">بارگذاری کارتابل ناموفق بود</p>
              <p>{inbox.error}</p>
              <button
                type="button"
                className="dashboard-cartable-more"
                disabled={inboxLoading}
                onClick={() => void loadInbox()}
              >
                تلاش دوباره
              </button>
            </div>
          ) : (
            <div className="dashboard-cartable-grid">
              <CartablePane
                title="وظایف"
                count={inboxCounts.tasks}
                Icon={ListChecks}
                loading={showCartableLoading}
                emptyTitle="وظیفه بازی ندارید"
                emptyHint="وقتی کاری به شما ارجاع شود، اینجا دیده می‌شود."
                tasks={visibleGeneralTasks}
                openingDocId={openingDocId}
                onOpen={handleOpenTask}
                onSeeAll={
                  generalTasks.length > visibleGeneralTasks.length
                    ? onOpenDocuments
                    : undefined
                }
                seeAllLabel={
                  generalTasks.length > visibleGeneralTasks.length
                    ? `مشاهده همه وظایف (${faNum(generalTasks.length)})`
                    : undefined
                }
              />
              <CartablePane
                title="اسناد نیازمند بررسی"
                count={inboxCounts.review}
                Icon={UserCheck}
                loading={showCartableLoading}
                emptyTitle="سندی برای بررسی نیست"
                emptyHint="تأیید و امضای در انتظار اینجا نمایش داده می‌شود."
                tasks={visibleReviewTasks}
                openingDocId={openingDocId}
                onOpen={handleOpenTask}
                onSeeAll={
                  reviewTasks.length > visibleReviewTasks.length
                    ? onOpenDocuments
                    : undefined
                }
                seeAllLabel={
                  reviewTasks.length > visibleReviewTasks.length
                    ? `مشاهده همه اسناد (${faNum(reviewTasks.length)})`
                    : undefined
                }
              />
            </div>
          )}
        </section>
      ) : null}

      <section className="dashboard-products-block" aria-label="مدیریت‌های سازمانی">
        <div className="dashboard-product-grid dashboard-product-grid-secondary">
          <DashboardProductCard {...casesProduct} />
        </div>
        <section className="dashboard-dadno-box" aria-label="دادنو">
          <div className="dashboard-dadno-head">
            <div className="dashboard-dadno-head-text">
              <img
                className="dashboard-dadno-logo"
                src="/brand/dadno-logo.png"
                alt="دادنو"
                width={96}
                height={40}
                decoding="async"
              />
              <p className="dashboard-dadno-subtitle">
                دستیار هوش مصنوعی حقوقی دادنو
              </p>
            </div>
          </div>
          <div className="dashboard-dadno-actions" role="group" aria-label="قابلیت‌های دادنو">
            {dadnoActions.map(({ mode, label, imageSrc }) => (
              <button
                key={mode}
                type="button"
                className="dashboard-dadno-action"
                onClick={() => {
                  if (!isAuthenticated) {
                    openLogin(() => onOpenDadnoAction(mode));
                    return;
                  }
                  onOpenDadnoAction(mode);
                }}
              >
                <img
                  className="dashboard-dadno-action-icon"
                  src={imageSrc}
                  alt=""
                  width={36}
                  height={36}
                  decoding="async"
                  aria-hidden
                />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </section>
      </section>

      {localDrafts.length ? (
        <section className="dashboard-section dashboard-muted-section" aria-label="پیش‌نویس‌های محلی">
          <div className="dashboard-section-title">
            <FilePenLine size={16} />
            <div>
              <h2>پیش‌نویس‌ها</h2>
              <p>{faNum(localDrafts.length)} مورد</p>
            </div>
          </div>
          <ul className="local-draft-list">
            {visibleLocalDrafts.map((d) => (
              <li key={d.id}>
                <button type="button" onClick={() => onOpenLocalDraft(d)}>
                  <span>{d.title}</span>
                  <small>ذخیره‌نشده</small>
                </button>
              </li>
            ))}
          </ul>
          {hiddenLocalDrafts.length ? (
            <details className="dashboard-collapsible">
              <summary>{faNum(hiddenLocalDrafts.length)} مورد دیگر</summary>
              <ul className="local-draft-list nested">
                {hiddenLocalDrafts.map((d) => (
                  <li key={d.id}>
                    <button type="button" onClick={() => onOpenLocalDraft(d)}>
                      <span>{d.title}</span>
                      <small>ذخیره‌نشده</small>
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </section>
      ) : null}

      {scannedDocs.length && onOpenScannedDoc ? (
        <section
          className="dashboard-section dashboard-muted-section"
          aria-label="اسناد اسکن‌شده"
        >
          <div className="dashboard-section-title">
            <ScanLine size={16} />
            <div>
              <h2>اسناد اسکن‌شده من</h2>
              <p>{faNum(scannedDocs.length)} مورد روی این دستگاه</p>
            </div>
          </div>
          <ul className="local-draft-list scan-dashboard-list">
            {scannedDocs.slice(0, 4).map((d) => (
              <li key={d.id}>
                <button type="button" onClick={() => onOpenScannedDoc(d.id)}>
                  <span className="scan-dashboard-row">
                    {d.thumbnailUrl ? (
                      <img src={d.thumbnailUrl} alt="" className="scan-dashboard-thumb" />
                    ) : null}
                    <span>{d.title}</span>
                  </span>
                  <small>{faNum(d.pageCount)} صفحه</small>
                </button>
              </li>
            ))}
          </ul>
          {onOpenScanLibrary ? (
            <button
              type="button"
              className="ghost-btn scan-dashboard-more"
              onClick={onOpenScanLibrary}
            >
              مشاهده همه / کتابخانه اسکن
            </button>
          ) : null}
        </section>
      ) : null}

      <section className="dashboard-section dashboard-tools-section dashboard-muted-section" aria-label="ابزارهای عمومی">
        <HomeToolsGrid onSelect={handleSelectTool} />
      </section>
    </div>
  );
}
